/* eslint-disable no-param-reassign */
import companies from '@/modules/Flexigo/services/companies';

/**
 * Namespaced Vuex module that owns fetching, caching, persisting and per-tab
 * loading state for the company settings page (FormBuilder-based tabs).
 *
 * State is keyed by an opaque `tabKey` of the form `<name>` or `<name>:<type>`
 * (e.g. `general`, `flexiRidePool:FLEXIRIDE_POOL_2`). The `<name>` part is
 * looked up in the local fetcher/persister registries so individual tab
 * components only need to declare a `tabKey` and provide `params`.
 *
 * Tab components and external callers should reference these keys via the
 * exported `SETTING_KEYS` constant (single source of truth).
 */
export const SETTING_KEYS = Object.freeze({
  BASIC_INFO: 'basicInfo',
  GENERAL: 'general',
  ADMINS: 'admins',
  SHUTTLE: 'shuttle',
  VANPOOL: 'vanpool',
  FLEXI_CAR: 'flexiCar',
  FLEXI_RIDE: 'flexiRide',
  FLEXI_RIDE_POOL: 'flexiRidePool:FLEXIRIDE_POOL',
  FLEXI_RIDE_POOL_2: 'flexiRidePool:FLEXIRIDE_POOL_2',
  FLEXI_RIDE_POOL_3: 'flexiRidePool:FLEXIRIDE_POOL_3',
  FLEXI_CARPOOL: 'flexiCarpool',
  PUBLIC_LINKS: 'publicLinks',
});

const splitKey = (key) => {
  const [name, type] = String(key || '').split(':');
  return { name, type };
};

const fetchers = {
  basicInfo: ({ companyId, subcompanyId }) =>
    subcompanyId ? companies.getSubCompanyBasicInfo(subcompanyId, companyId) : companies.getCompanyBasicInfo(companyId),
  general: ({ accountType, companyId, subcompanyId }) =>
    companies.getSettings(accountType, 'general', companyId, subcompanyId),
  admins: ({ accountType, companyId, subcompanyId }) =>
    companies.getSettings(accountType, 'admins', companyId, subcompanyId),
  shuttle: ({ accountType, companyId, subcompanyId }) =>
    companies.getSettings(accountType, 'shuttle-vanpool', companyId, subcompanyId),
  vanpool: ({ accountType, companyId, subcompanyId }) =>
    companies.getSettings(accountType, 'vanpool', companyId, subcompanyId),
  flexiCarpool: ({ accountType, companyId, subcompanyId }) =>
    companies.getSettings(accountType, 'carpool', companyId, subcompanyId),
  flexiCar: ({ companyId, subcompanyId }) => companies.getFlexiCarSettings(companyId, subcompanyId),
  flexiRide: ({ companyId, subcompanyId }) => companies.getFlexiRideSettings(companyId, subcompanyId),
  flexiRidePool: ({ type, companyId, subcompanyId, isRider }) =>
    companies.getFlexiRidePoolSettings(type, companyId, subcompanyId, isRider),
  publicLinks: ({ accountType, companyId, subcompanyId }) =>
    companies.getSettings(accountType, 'public-links', companyId, subcompanyId),
};

const persisters = {
  basicInfo: (payload) => companies.updateCompanyBasicInfo(payload),
  general: (payload, { accountType, companyId, subcompanyId }) =>
    companies.updateSettings(accountType, 'general', payload, companyId, subcompanyId),
  admins: (payload, { accountType, companyId, subcompanyId }) =>
    companies.updateSettings(accountType, 'admins', payload, companyId, subcompanyId),
  shuttle: (payload, { accountType, companyId, subcompanyId }) =>
    companies.updateSettings(accountType, 'shuttle-vanpool', payload, companyId, subcompanyId),
  vanpool: (payload, { accountType, companyId, subcompanyId }) =>
    companies.updateSettings(accountType, 'vanpool', payload, companyId, subcompanyId),
  flexiCarpool: (payload, { accountType, companyId, subcompanyId }) =>
    companies.updateSettings(accountType, 'carpool', payload, companyId, subcompanyId),
  flexiCar: (payload, { companyId, subcompanyId }) =>
    companies.updateFlexiCarSettings(payload, companyId, subcompanyId),
  flexiRide: (payload, { companyId, subcompanyId }) =>
    companies.updateFlexiRideSettings(payload, companyId, subcompanyId),
  flexiRidePool: (payload, { type, companyId, subcompanyId }) =>
    companies.updateFlexiRidePoolSettings(type, { ...payload, type }, companyId, subcompanyId),
  publicLinks: (payload, { accountType, companyId, subcompanyId }) =>
    companies.updateSettings(accountType, 'public-links', payload, companyId, subcompanyId),
};

const resolveFetcher = (key, params) => {
  const { name, type } = splitKey(key);
  const fn = fetchers[name];
  if (!fn) throw new Error(`No settings fetcher registered for tab "${name}"`);
  if (name === 'flexiRidePool') {
    return () => fn({ ...(params || {}), type: params?.type ?? type, isRider: params?.isRider ?? false });
  }
  return () => fn({ ...(params || {}), type: params?.type ?? type });
};

const resolvePersister = (key, params) => {
  const { name, type } = splitKey(key);
  const fn = persisters[name];
  if (!fn) throw new Error(`No settings persister registered for tab "${name}"`);
  return (payload) => fn(payload, { ...(params || {}), type: params?.type ?? type });
};

export const knownKeys = new Set(Object.values(SETTING_KEYS));

// Cached data is scoped to the (company, subcompany, type) triple. Without this,
// opening company A then B would hand B the data cached for A — wrong cards,
// wrong tab visibility, and saves posting A's link ids under B's company id.
const makeContextKey = (params) => {
  const { companyId = '', subcompanyId = '', type = '' } = params || {};
  return `${companyId}:${subcompanyId}:${type}`;
};

const defaultTabState = () => ({
  data: null,
  loading: false,
  saving: false,
  loaded: false,
  error: null,
  context: '',
});

export default {
  namespaced: true,
  state: {
    byKey: {},
    activeTabKey: null,
  },
  getters: {
    tabState: (state) => (key) => {
      if (key && !knownKeys.has(key)) {
        console.warn(`[settingsTabs] Unknown tab key: "${key}"`);
        return defaultTabState();
      }
      return state.byKey[key] || defaultTabState();
    },
    data: (_state, getters) => (key) => getters.tabState(key).data,
    isLoading: (_state, getters) => (key) => Boolean(getters.tabState(key).loading || getters.tabState(key).saving),
    isActiveTabLoading: (state, getters) => (state.activeTabKey ? getters.isLoading(state.activeTabKey) : false),
  },
  mutations: {
    SET_ACTIVE_TAB(state, key) {
      state.activeTabKey = key || null;
    },
    SET_TAB(state, { key, patch }) {
      const prev = state.byKey[key] || defaultTabState();
      state.byKey = { ...state.byKey, [key]: { ...prev, ...patch } };
    },
    CLEAR_TAB(state, key) {
      const next = { ...state.byKey };
      delete next[key];
      state.byKey = next;
    },
  },
  actions: {
    setActiveTab({ commit }, key) {
      commit('SET_ACTIVE_TAB', key);
    },
    async fetchTab({ state, commit }, { key, params, force = false } = {}) {
      if (!key) return null;
      const current = state.byKey[key];
      const context = makeContextKey(params);
      if (current?.loaded && !force && current.context === context) return current.data;
      const fetcher = resolveFetcher(key, params);
      // Context change: wipe stale data AND reset the saving flag so a save still
      // pending from the previous context does not leave this slot stuck once its
      // completion is ignored below.
      commit('SET_TAB', { key, patch: { loading: true, saving: false, error: null, data: null, loaded: false, context } });
      try {
        const data = await fetcher();
        // Guard: a newer fetch (different context) may have taken this slot over
        // while we were awaiting. Dropping the stale commit keeps the UI showing
        // the current company's data instead of overwriting it with A's result.
        if (state.byKey[key]?.context !== context) return data;
        commit('SET_TAB', { key, patch: { data, loaded: true, loading: false, context } });
        return data;
      } catch (error) {
        if (state.byKey[key]?.context !== context) throw error;
        commit('SET_TAB', { key, patch: { loading: false, error } });
        throw error;
      }
    },
    async saveTab({ state, commit }, { key, payload, params } = {}) {
      if (!key) return null;
      const persister = resolvePersister(key, params);
      const context = makeContextKey(params);
      commit('SET_TAB', { key, patch: { saving: true, error: null, context } });
      try {
        const data = (await persister(payload)) ?? payload;
        // Guard: user may have navigated to a different company while save was
        // pending. Writing A's response into B's slot would corrupt visibility
        // checks and the next save payload.
        if (state.byKey[key]?.context !== context) return data;
        commit('SET_TAB', { key, patch: { data, loaded: true, saving: false, context } });
        return data;
      } catch (error) {
        if (state.byKey[key]?.context !== context) throw error;
        commit('SET_TAB', { key, patch: { saving: false, error } });
        throw error;
      }
    },
    refreshTab({ dispatch }, { key, params } = {}) {
      return dispatch('fetchTab', { key, params, force: true });
    },
    invalidateTab({ commit }, key) {
      commit('CLEAR_TAB', key);
    },
  },
};

/* eslint-enable no-param-reassign */
